"use client";

import Link from "next/link";
import { useState } from "react";
import { FaChevronDown } from "react-icons/fa";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";

interface Section {
  title: string;
  subsections: { heading: string; content: string[] }[];
}

const sections: Section[] = [
  {
    title: "Content",
    subsections: [
      {
        heading: "Your Responsibility",
        content: [
          'Our Service allows you to post, link, store, share and otherwise make available certain information, text, graphics, videos, or other material ("Content"). You are responsible for the Content that you post to the Service, including its legality, reliability, and appropriateness.',
        ],
      },
      {
        heading: "License Grant",
        content: [
          "By posting Content to the Service, you grant us the right and license to use, modify, publicly perform, publicly display, reproduce, and distribute such Content on and through the Service. You retain any and all of your rights to any Content you submit, post or display on or through the Service and you are responsible for protecting those rights.",
        ],
      },
      {
        heading: "Warranties",
        content: [
          "You represent and warrant that: (i) the Content is yours (you own it) or you have the right to use it and grant us the rights and license as provided in these Terms, and (ii) the posting of your Content on or through the Service does not violate the privacy rights, publicity rights, copyrights, contract rights or any other rights of any person.",
        ],
      },
    ],
  },
  {
    title: "Intellectual Property",
    subsections: [
      {
        heading: "Our Rights",
        content: [
          "The Service and its original content (excluding Content provided by users), features and functionality are and will remain the exclusive property of Hype Grow, Unipessoal Lda and its licensors. The Service is protected by copyright, trademark, and other laws of both Bulgaria and foreign countries. Our trademarks and trade dress may not be used in connection with any product or service without the prior written consent of Hype Grow, Unipessoal Lda.",
        ],
      },
    ],
  },
  {
    title: "Links to Other Web Sites",
    subsections: [
      {
        heading: "Third Party Content",
        content: [
          "Our Service contains links to third-party web sites or services that are not owned or controlled by Hype Grow, Unipessoal Lda. Hype Grow, Unipessoal Lda has no control over and assumes no responsibility for, the content, privacy policies, or practices of any third-party web sites or services.",
          "We strongly advise you to read the terms and conditions and privacy policies of any third-party web sites or services that you visit.",
        ],
      },
    ],
  },
  {
    title: "Termination",
    subsections: [
      {
        heading: "Account Suspension",
        content: [
          "We may terminate or suspend your account immediately, without prior notice or liability, for any reason whatsoever, including without limitation if you breach the Terms. Upon termination, your right to use the Service will immediately cease.",
        ],
      },
    ],
  },
  {
    title: "Limitation of Liability",
    subsections: [
      {
        heading: "No Liability",
        content: [
          "In no event shall Hype Grow, Unipessoal Lda, nor its directors, employees, partners, agents, suppliers, or affiliates, be liable for any indirect, incidental, special, consequential or punitive damages, including without limitation, loss of profits, data, use, goodwill, or other intangible losses, resulting from your access to or use of the Service.",
        ],
      },
    ],
  },
  {
    title: "Disclaimer",
    subsections: [
      {
        heading: "As-Is Service",
        content: [
          'Your use of the Service is at your sole risk. The Service is provided on an "AS IS" and "AS AVAILABLE" basis. The Service is provided without warranties of any kind, whether express or implied, including, but not limited to, implied warranties of merchantability, fitness for a particular purpose, non-infringement or course of performance.',
        ],
      },
    ],
  },
  {
    title: "Governing Law",
    subsections: [
      {
        heading: "Bulgaria Jurisdiction",
        content: [
          "These Terms shall be governed and construed in accordance with the laws of Bulgaria, without regard to its conflict of law provisions. Our failure to enforce any right or provision of these Terms will not be considered a waiver of those rights.",
        ],
      },
    ],
  },
  {
    title: "Changes",
    subsections: [
      {
        heading: "Modifications",
        content: [
          "We reserve the right, at our sole discretion, to modify or replace these Terms at any time. If a revision is material, we will try to provide at least 30 days notice prior to any new terms taking effect. By continuing to access or use our Service after those revisions become effective, you agree to be bound by the revised terms.",
        ],
      },
    ],
  },
];

function CollapsibleSection({
  section,
  index,
}: {
  section: Section;
  index: number;
}) {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <div className="overflow-hidden rounded-xl border border-white/10 bg-gradient-to-br from-[#1e1e1e] to-[#0f0f0f] hover:border-white/20 transition-all duration-300">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center justify-between p-6 hover:bg-white/5 transition-colors duration-300 group"
      >
        <div className="flex items-center gap-4">
          <span className="text-2xl font-bold text-blue-400 w-10 text-center">
            0{index + 1}
          </span>
          <h3 className="text-xl font-bold text-left group-hover:text-blue-300 transition-colors duration-300">
            {section.title}
          </h3>
        </div>
        <FaChevronDown
          className={`text-blue-400 transition-transform duration-300 ${
            isOpen ? "rotate-180" : ""
          }`}
          size={20}
        />
      </button>

      {isOpen && (
        <div className="border-t border-white/10 px-6 py-4 space-y-4 bg-black/20">
          {section.subsections.map((subsection, subIndex) => (
            <div key={subIndex}>
              <h4 className="font-semibold text-base text-blue-200 mb-2">
                {subsection.heading}
              </h4>
              {subsection.content.map((paragraph, pIndex) => (
                <p
                  key={pIndex}
                  className="text-gray-300 text-sm leading-relaxed mb-3 last:mb-0"
                >
                  {paragraph}
                </p>
              ))}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

const TermsAndConditions = () => {
  return (
    <div className="min-h-screen w-full flex flex-col bg-[#141414] text-white">
      <Navbar />
      <main className="w-full flex flex-col items-center justify-start pt-24">

          {/* Header */}
          <section className="w-full px-4 py-8">
            <div className="max-w-4xl mx-auto">
              <h1 className="text-4xl md:text-5xl font-extrabold text-center mb-4 tracking-tight">
                Terms & Conditions
              </h1>
              <div className="border-l-4 border-blue-500 pl-6 bg-blue-500/10 rounded-r-lg p-4 mb-6">
                <p className="text-gray-200 leading-relaxed">
                  These Terms and Conditions govern your relationship with
                  Casinob8.com website (the "Service") operated by Hype Grow,
                  Unipessoal Lda. By accessing or using the Service, you agree
                  to be bound by these Terms. Please read carefully before
                  proceeding.
                </p>
              </div>
            </div>
          </section>

          {/* Introduction Section */}
          <section className="w-full px-4 py-4">
            <div className="max-w-4xl mx-auto rounded-xl border border-white/10 bg-gradient-to-br from-[#1e1e1e] to-[#0f0f0f] p-6">
              <h2 className="text-2xl font-bold mb-4 text-blue-300">
                📋 Key Points
              </h2>
              <ul className="space-y-3">
                <li className="flex items-start gap-3">
                  <span className="text-blue-400 font-bold mt-1">✓</span>
                  <p className="text-gray-300 text-sm">
                    Your access to the Service is conditioned on your acceptance
                    of and compliance with these Terms.
                  </p>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-blue-400 font-bold mt-1">✓</span>
                  <p className="text-gray-300 text-sm">
                    These Terms apply to all visitors, users and others who
                    access or use the Service.
                  </p>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-blue-400 font-bold mt-1">✓</span>
                  <p className="text-gray-300 text-sm">
                    If you disagree with any part of these Terms, you may not
                    access the Service.
                  </p>
                </li>
              </ul>
            </div>
          </section>

          {/* Collapsible Sections */}
          <section className="w-full px-4 py-8">
            <div className="max-w-4xl mx-auto">
              <div className="space-y-4">
                {sections.map((section, index) => (
                  <CollapsibleSection
                    key={index}
                    section={section}
                    index={index}
                  />
                ))}
              </div>
            </div>
          </section>

          {/* Contact Section */}
          <section className="w-full px-4 py-12">
            <div className="max-w-4xl mx-auto">
              <div className="rounded-2xl bg-gradient-to-r from-blue-600/20 to-purple-600/20 border border-blue-500/30 p-8 backdrop-blur text-center">
                <h2 className="text-2xl md:text-3xl font-bold mb-4">
                  Questions?
                </h2>
                <p className="text-gray-300 mb-6">
                  If you have any questions about these Terms and Conditions,
                  please don't hesitate to contact us.
                </p>
                <Link
                  href="/imprint"
                  className="inline-block px-8 py-3 bg-gradient-to-r from-blue-500 to-purple-600 rounded-lg font-semibold hover:shadow-lg hover:shadow-blue-500/50 transition-all duration-300 hover:scale-105"
                >
                  Contact Information
                </Link>
              </div>
            </div>
          </section>
        </main>
      <Footer />
    </div>
  );
};

export default TermsAndConditions;
